/**
 * What the browser downloads of the message catalog (ALU-662).
 *
 * The root layout used to hand the whole catalog to the client provider, and
 * about half of it is admin copy that member and public screens never read.
 * `splitClientMessages` cuts it in two:
 *
 *   member: what every route's provider gets (the root layout).
 *   admin:  the admin namespaces, given only to the admin routes through
 *           components/i18n/admin-messages-provider.tsx, which merges them
 *           over what the root provider already holds.
 *
 * Server components are not affected: `getTranslations` reads the full catalog
 * from lib/i18n/request.ts. Only `useTranslations` in client components needs
 * the namespace to be in the provider.
 *
 * tests/i18n/client-messages.test.ts walks the import graph from every
 * non-admin route and fails if a client component reachable from one of them
 * reads an admin namespace that is not in SHARED_ADMIN_KEYS /
 * SHARED_ENCUENTRO_AREAS, so a new use in a member screen cannot ship a
 * `MISSING_MESSAGE` by surprise.
 */
type MessageTree = Record<string, unknown>

/**
 * Keys under `admin` that client components outside the admin routes read:
 * the view-as banner (member layout), the member profile sheet and the logo
 * upload (the join flow).
 */
export const SHARED_ADMIN_KEYS = [
  'viewAs',
  'memberDetail',
  'settings.logoUpdated',
  'settings.uploadTooLarge',
  'settings.uploadWrongType',
  'settings.errorUploadingLogo',
  'settings.organizationLogo',
  'settings.uploadLogo',
  'settings.logoRecommended',
] as const

/** Encuentro areas that are admin copy but are also read outside the admin routes (the settings sections). */
export const SHARED_ENCUENTRO_AREAS: readonly string[] = ['adminKit']

/** True for the `encuentro.<area>` files that only the admin routes read. */
export function isAdminOnlyEncuentroArea(area: string): boolean {
  if (SHARED_ENCUENTRO_AREAS.includes(area)) return false
  return area.startsWith('admin') || area === 'platformAdmin' || area === 'institutionWizard'
}

function isTree(value: unknown): value is MessageTree {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function readPath(tree: MessageTree, path: string): unknown {
  let node: unknown = tree
  for (const part of path.split('.')) {
    if (!isTree(node) || !(part in node)) return undefined
    node = node[part]
  }
  return node
}

function writePath(tree: MessageTree, path: string, value: unknown): void {
  const parts = path.split('.')
  let node = tree
  for (const part of parts.slice(0, -1)) {
    const next = node[part]
    if (isTree(next)) node = next
    else {
      const created: MessageTree = {}
      node[part] = created
      node = created
    }
  }
  node[parts[parts.length - 1]] = value
}

/** Recursive merge: `over` wins on leaves, trees merge. Pure. */
export function mergeMessageTrees(base: MessageTree, over: MessageTree): MessageTree {
  const out: MessageTree = { ...base }
  for (const [key, value] of Object.entries(over)) {
    const current = out[key]
    out[key] = isTree(current) && isTree(value) ? mergeMessageTrees(current, value) : value
  }
  return out
}

/** Namespaces that never reach the client (server-only copy). */
const SERVER_ONLY = ['email', 'notifications'] as const

/**
 * Splits the full catalog (lib/i18n/encuentro-messages.ts loadMessages) into
 * what the root provider sends and what the admin routes add. Nothing is lost:
 * `mergeMessageTrees(member, admin)` equals the catalog without SERVER_ONLY.
 */
export function splitClientMessages(all: MessageTree): { member: MessageTree; admin: MessageTree } {
  const { admin: adminAll, encuentro: encuentroAll, ...rest } = all
  for (const key of SERVER_ONLY) delete rest[key]

  const member: MessageTree = { ...rest }
  const admin: MessageTree = {}

  if (isTree(adminAll)) {
    const shared: MessageTree = {}
    for (const path of SHARED_ADMIN_KEYS) {
      const value = readPath(adminAll, path)
      if (value !== undefined) writePath(shared, path, value)
    }
    member.admin = shared
    admin.admin = adminAll
  }

  if (isTree(encuentroAll)) {
    const memberAreas: MessageTree = {}
    const adminAreas: MessageTree = {}
    for (const [area, value] of Object.entries(encuentroAll)) {
      if (isAdminOnlyEncuentroArea(area)) adminAreas[area] = value
      else memberAreas[area] = value
    }
    member.encuentro = memberAreas
    admin.encuentro = adminAreas
  }

  return { member, admin }
}
